import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/form';
import { WorkflowWizardData, WorkflowStepData } from '../workflow-wizard';

interface ApprovalStepsStepProps {
  data: WorkflowWizardData;
  onChange: (updates: Partial<WorkflowWizardData>) => void;
  /** When true, a workflow- or division-level setting already forces "Require explicit Submit" on for every step - the per-step checkbox is shown checked and disabled. */
  forceRequireExplicitSubmit?: boolean;
}

export function ApprovalStepsStep({ data, onChange, forceRequireExplicitSubmit }: ApprovalStepsStepProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const addStep = () => {
    const newStep: WorkflowStepData = {
      id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      step: data.steps.length + 1,
      stepType: 'approval',
      name: `Approval Step ${data.steps.length + 1}`,
      description: '',
      canEditQuantity: false,
      allowQuantityIncrease: false,
      approvalType: 'any',
      requiredApprovals: 1,
      approverUserIds: [],
      approverEmails: [],
      autoEscalation: false,
      escalationHours: 24,
      requireExplicitSubmit: false
    };
    onChange({ steps: [...data.steps, newStep] });
    const newExpanded = new Set(expandedSteps);
    newExpanded.add(newStep.id);
    setExpandedSteps(newExpanded);
  };

  const removeStep = (stepId: string) => {
    const updatedSteps = data.steps
      .filter(s => s.id !== stepId)
      .map((s, index) => ({ ...s, step: index + 1 }));
    
    onChange({ steps: updatedSteps });
    const newExpanded = new Set(expandedSteps);
    newExpanded.delete(stepId);
    setExpandedSteps(newExpanded);
  };

  const updateStep = (stepId: string, updates: Partial<WorkflowStepData>) => {
    const updatedSteps = data.steps.map(s =>
      s.id === stepId ? { ...s, ...updates } : s
    );
    onChange({ steps: updatedSteps });
  };

  const moveStep = (stepId: string, direction: 'up' | 'down') => {
    const index = data.steps.findIndex(s => s.id === stepId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === data.steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...data.steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    // Update step numbers
    const updatedSteps = newSteps.map((s, idx) => ({ ...s, step: idx + 1 }));
    onChange({ steps: updatedSteps });
  };

  const toggleExpanded = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Approval Steps
        </h3>
        <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">
          Define the approval steps for your workflow. Orders will progress through these steps in sequence.
        </p>
      </div>

      <div className="space-y-4">
        {data.steps.map((step, index) => {
          const isExpanded = expandedSteps.has(step.id);
          
          return (
            <div
              key={step.id}
              className="border border-gray-300 dark:border-secondary-600 rounded-lg overflow-hidden"
            >
              {/* Step Header */}
              <div className="bg-gray-50 dark:bg-secondary-900 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="cursor-move text-gray-400 dark:text-secondary-500">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {step.step}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">{step.name}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveStep(step.id, 'up')}
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  )}
                  {index < data.steps.length - 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveStep(step.id, 'down')}
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(step.id)}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStep(step.id)}
                    className="text-red-600 dark:text-red-400 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Step Details */}
              {isExpanded && (
                <div className="p-4 space-y-4 bg-white dark:bg-secondary-800">
                  <Input
                    label="Step Name"
                    value={step.name}
                    onChange={(e) => updateStep(step.id, { name: e.target.value })}
                    placeholder="e.g., Manager Approval"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                      Description
                    </label>
                    <textarea
                      value={step.description}
                      onChange={(e) => updateStep(step.id, { description: e.target.value })}
                      placeholder="Describe this approval step..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                      Step Type
                    </label>
                    <select
                      value={step.stepType}
                      onChange={(e) => updateStep(step.id, { stepType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    >
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="notification">Notification</option>
                      <option value="fulfillment">Fulfillment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                      Approval Type
                    </label>
                    <select
                      value={step.approvalType}
                      onChange={(e) => updateStep(step.id, { 
                        approvalType: e.target.value as 'any' | 'all' | 'specific-count',
                        requiredApprovals: e.target.value === 'specific-count' ? 1 : undefined
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    >
                      <option value="any">Any single approver</option>
                      <option value="all">All approvers must approve</option>
                      <option value="specific-count">Specific number of approvals</option>
                    </select>
                  </div>

                  {step.approvalType === 'specific-count' && (
                    <Input
                      label="Required Number of Approvals"
                      type="number"
                      min={1}
                      value={step.requiredApprovals || 1}
                      onChange={(e) => updateStep(step.id, { 
                        requiredApprovals: parseInt(e.target.value) || 1 
                      })}
                    />
                  )}

                  <div className="border-t border-gray-200 dark:border-secondary-700 pt-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={step.canEditQuantity}
                        onChange={(e) => updateStep(step.id, { canEditQuantity: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Approver can edit quantities at this step
                      </span>
                    </label>
                    <label className={`flex items-center gap-2 mt-2 ml-6 ${step.canEditQuantity ? '' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={step.allowQuantityIncrease ?? false}
                        onChange={(e) => updateStep(step.id, { allowQuantityIncrease: e.target.checked })}
                        disabled={!step.canEditQuantity}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm text-gray-700 dark:text-secondary-200">
                        Allow increasing quantities (uncheck to only allow decreasing)
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={step.orderLevelOnly ?? false}
                        onChange={(e) => updateStep(step.id, { orderLevelOnly: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Order-level approval only (approvers approve or reject the entire order, not individual line items)
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className={`flex items-center gap-2 ${forceRequireExplicitSubmit ? 'cursor-not-allowed opacity-75' : ''}`}>
                      <input
                        type="checkbox"
                        checked={forceRequireExplicitSubmit || (step.requireExplicitSubmit ?? false)}
                        onChange={(e) => updateStep(step.id, { requireExplicitSubmit: e.target.checked })}
                        disabled={forceRequireExplicitSubmit}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Require explicit "Submit" (approvers draft decisions with Undo/Discard before committing)
                        {forceRequireExplicitSubmit && <span className="italic text-gray-500 dark:text-secondary-400"> - enforced by workflow/division setting</span>}
                      </span>
                    </label>
                  </div>

                  <div className="border-t border-gray-200 dark:border-secondary-700 pt-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={step.autoEscalation}
                        onChange={(e) => updateStep(step.id, { autoEscalation: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Enable auto-escalation
                      </span>
                    </label>
                  </div>

                  {step.autoEscalation && (
                    <Input
                      label="Escalation Time (hours)"
                      type="number"
                      min={1}
                      value={step.escalationHours || 24}
                      onChange={(e) => updateStep(step.id, { 
                        escalationHours: parseInt(e.target.value) || 24 
                      })}
                    />
                  )}

                  <div className="border-t border-gray-200 dark:border-secondary-700 pt-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Conditions</h4>
                    <Input
                      label="Amount Threshold (optional)"
                      type="number"
                      min={0}
                      step="0.01"
                      value={step.amountThreshold ?? ''}
                      onChange={(e) => updateStep(step.id, {
                        amountThreshold: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                      placeholder="e.g., 1000.00"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                        Skip if already approved by role
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(step.skipIfApprovedBy ?? []).map(role => (
                          <span
                            key={role}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 text-xs"
                          >
                            {role}
                            <button
                              type="button"
                              onClick={() => updateStep(step.id, {
                                skipIfApprovedBy: (step.skipIfApprovedBy ?? []).filter(r => r !== role)
                              })}
                              className="ml-1 text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Role name (e.g., Admin, DivisionAdmin)"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const input = e.target as HTMLInputElement;
                              const trimmed = input.value.trim();
                              if (trimmed && !(step.skipIfApprovedBy ?? []).includes(trimmed)) {
                                updateStep(step.id, { skipIfApprovedBy: [...(step.skipIfApprovedBy ?? []), trimmed] });
                              }
                              input.value = '';
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            const input = (e.target as HTMLElement).closest('div')?.querySelector('input') as HTMLInputElement;
                            if (input) {
                              const trimmed = input.value.trim();
                              if (trimmed && !(step.skipIfApprovedBy ?? []).includes(trimmed)) {
                                updateStep(step.id, { skipIfApprovedBy: [...(step.skipIfApprovedBy ?? []), trimmed] });
                              }
                              input.value = '';
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {data.steps.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-secondary-600 rounded-lg">
            <p className="text-gray-500 dark:text-secondary-400 mb-4">No approval steps yet</p>
            <p className="text-sm text-gray-400 dark:text-secondary-500 mb-4">
              Click the button below to add your first approval step
            </p>
          </div>
        )}

        <Button
          variant="outline"
          onClick={addStep}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Approval Step
        </Button>
      </div>

      {data.steps.length > 0 && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-medium text-green-900">
                {data.steps.length} approval {data.steps.length === 1 ? 'step' : 'steps'} configured
              </h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                You can reorder steps by using the up/down arrows, or click to expand and configure each step's details.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
